import { RefObject, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { useTheme, lighten } from '@material-ui/core'
import { withScriptJs } from 'components/ScriptJs'
import InfoWindow from './InfoWindow'

import createClusterElement from './cluster-dom-builder'
import { cluster, highSchool } from './types'

const GOOGLE_QUERY_FN = (key: string) =>
  new URLSearchParams({
    v: 'beta',
    libraries: 'maps,marker',
    key,
  }).toString()

const displayableClusterValue = (value: number) => {
  if (value > 1000) {
    return `${Math.round(value / 100) / 10}k`
  }
  return value.toLocaleString()
}

type MarkerTracker = Record<string, { marker: google.maps.marker.AdvancedMarkerElement } | undefined>

export const withGoogleMapsApi = (key: string) =>
  function <T>(comp: T): T {
    return withScriptJs(`https://maps.googleapis.com/maps/api/js?${GOOGLE_QUERY_FN(key)}`)(comp) as T
  }

const init = async (ref: RefObject<HTMLDivElement>, opts: { center: { lat: number; lng: number }; zoom: number }) => {
  const google = window.google
  if (!google) {
    throw new Error('window.google is not defined. Is the google maps api available on the page?')
  }

  const { center, zoom } = opts

  const Map = google.maps.Map

  if (!ref.current) {
    return
  }
  return new Map(ref.current, {
    mapId: '1089528a3d57c5d9',
    center: center,
    zoom: zoom,
    minZoom: 4,
    mapTypeControl: false,
    fullscreenControl: false,
  })
}

function GoogleMap<T extends highSchool>({
  center,
  zoom,
  clusters,
  highSchools,
  onSelectHighSchool,
  onZoomChange,
  onCenterChange,
}: {
  center: { lat: number; lng: number }
  zoom: number
  clusters: cluster[]
  highSchools: T[]
  onSelectHighSchool: (hs: T) => void
  onZoomChange: (zoom: number, lat: number, lng: number) => void
  onCenterChange: (lat: number, lng: number) => void
}) {
  const map = useRef<google.maps.Map>()
  const infoWindow = useRef<google.maps.InfoWindow>()
  const div = useRef<HTMLDivElement>(null)
  const clusterMarkerLookup = useRef<MarkerTracker>({})
  const highSchoolMarkerLookup = useRef<MarkerTracker>({})
  const defaultsRef = useRef({
    center,
    zoom,
  })

  const [mapInitialized, setMapInitialized] = useState(false)
  const [selectedItem, setSelectedItem] = useState<highSchool>()

  const callbacks = useRef<{
    onZoomChange: typeof onZoomChange
    onCenterChange: typeof onCenterChange
    onSelectHighSchool: typeof onSelectHighSchool
  }>({ onZoomChange: onZoomChange, onCenterChange: onCenterChange, onSelectHighSchool: onSelectHighSchool })

  // re-init callbacks into ref to avoid effect impact
  callbacks.current.onZoomChange = onZoomChange
  callbacks.current.onCenterChange = onCenterChange
  callbacks.current.onSelectHighSchool = onSelectHighSchool

  useEffect(() => {
    if (map.current) {
      return
    }
    const fn = async () => {
      const myMap = await init(div, {
        center: defaultsRef.current.center,
        zoom: defaultsRef.current.zoom,
      })
      if (!myMap) {
        return
      }
      map.current = myMap
      myMap.addListener('zoom_changed', () => {
        const zoom = myMap.getZoom()
        const center = myMap.getCenter()
        if (zoom === undefined || center === undefined) {
          return
        }
        callbacks.current.onZoomChange(zoom, center.lat(), center.lng())
      })
      myMap.addListener('dragend', () => {
        const center = myMap.getCenter()
        if (center === undefined) {
          return
        }
        callbacks.current.onCenterChange(center.lat(), center.lng())
      })
      setMapInitialized(true)
    }
    fn()
  }, [])

  const { lat, lng } = center
  useEffect(() => {
    if (map.current) {
      map.current.panTo(new google.maps.LatLng(lat, lng))
      map.current.setZoom(zoom)
    }
  }, [lat, lng, zoom])

  const theme = useTheme()

  useEffect(() => {
    const google = window.google
    if (!mapInitialized) {
      return
    }
    let infoWin = infoWindow.current

    if (!infoWin) {
      infoWin = new google.maps.InfoWindow({
        content: document.createElement('div'),
        headerContent: document.createElement('div'),
      })
      infoWindow.current = infoWin
    }

    const existingClustersToRemove = { ...clusterMarkerLookup.current }
    const maxVal = clusters[0]?.value || 0
    const minVal = clusters[clusters.length - 1]?.value || 0

    clusters.forEach((cluster) => {
      const existing = existingClustersToRemove[cluster.id]
      delete existingClustersToRemove[cluster.id]
      let marker = existing?.marker

      if (marker === undefined) {
        marker = new google.maps.marker.AdvancedMarkerElement({
          map: map.current,
          title: `${cluster.count} schools`,
          gmpClickable: true,
        })
        marker.addEventListener('gmp-click', () => {
          const zoom = map.current?.getZoom()
          if (zoom === undefined) {
            return
          }
          const position = marker?.position
          if (!position) {
            return
          }
          const lat = typeof position.lat === 'function' ? position.lat() : position.lat
          const lng = typeof position.lng === 'function' ? position.lng() : position.lng
          callbacks.current.onZoomChange(zoom + 1, lat, lng)
        })
      }
      const percentage = Math.log10(cluster.value) / Math.log10(maxVal - minVal)
      const scale = Number.isNaN(percentage) ? 1 : Math.max(percentage, 0)
      const svgEl = createClusterElement(displayableClusterValue(cluster.value), {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.common.white,
        scale,
      })
      marker.content = svgEl
      marker.position = new google.maps.LatLng({ lat: cluster.lat, lng: cluster.lon })

      clusterMarkerLookup.current[cluster.id] = { marker }
    })

    const existingHighSchools = { ...highSchoolMarkerLookup.current }
    highSchools.forEach((hs) => {
      const existing = existingHighSchools[hs.id]
      delete existingHighSchools[hs.id]
      let marker = existing?.marker
      if (!marker) {
        marker = new google.maps.marker.AdvancedMarkerElement({
          map: map.current,
          position: {
            lat: hs.lat,
            lng: hs.lng,
          },
          gmpClickable: true,
        })
        marker.addEventListener('gmp-click', () => {
          setSelectedItem(hs)
          infoWin?.open(map.current, marker)

          // defer callback so that the click event bubbles before react updates occur
          // (click away listener had issues if it was in the same digest cycle)
          setTimeout(() => {
            callbacks.current.onSelectHighSchool(hs)
          })
        })
      }
      const hsPin = new google.maps.marker.PinElement({
        background: theme.palette.grey[500],
        borderColor: theme.palette.grey[800],
        glyphColor: theme.palette.grey[800],
      })
      if (hs.isActive) {
        hsPin.background = lighten(theme.palette.primary.main, 0.3)
        hsPin.borderColor = theme.palette.primary.main
        hsPin.glyphColor = theme.palette.primary.main
      }
      marker.content = hsPin.element
      marker.position = new google.maps.LatLng({ lat: hs.lat, lng: hs.lng })
      highSchoolMarkerLookup.current[hs.id] = { marker }
    })

    Object.entries(existingClustersToRemove).forEach(([id, val]) => {
      if (!val) return
      val.marker.map = null
      clusterMarkerLookup.current[id] = undefined
    })
    Object.entries(existingHighSchools).forEach(([id, val]) => {
      if (!val) return
      val.marker.map = null
      highSchoolMarkerLookup.current[id] = undefined
    })
  }, [mapInitialized, theme, clusters, highSchools])

  const infoWindowHeaderContent = infoWindow.current?.getHeaderContent()
  const infoWindowContent = infoWindow.current?.getContent()

  return (
    <>
      {infoWindowHeaderContent &&
        ReactDOM.createPortal(
          selectedItem ? (
            <InfoWindow
              highSchool={selectedItem}
              header
            />
          ) : null,
          infoWindowHeaderContent as HTMLDivElement,
        )}
      {infoWindowContent &&
        ReactDOM.createPortal(
          selectedItem ? <InfoWindow highSchool={selectedItem} /> : null,
          infoWindowContent as HTMLDivElement,
        )}
      <div
        ref={div}
        style={{
          height: '100%',
          width: '100%',
        }}
      ></div>
    </>
  )
}

export default GoogleMap
